import { formatCoachingTime } from '@efdigital/ll-addon-coaching';
import CoacheeNameCard from '@efdigital/ll-xceed-v2/components/coaching/coach/coachee-name-card.gts';
import { DashboardStat } from '@efdigital/ll-xceed-v2/components/coaching/dashboard-stat.gts';
import type { CoachingCoachCoacheesCoacheeModel } from '@efdigital/ll-xceed-v2/routes/coaching/coach/coachees/coachee';
import { chemistryStatus as resolveChemistryStatus } from '@efdigital/ll-xceed-v2/utils/coaching/coach-coachees';
import { PAGE_WRAPPER } from '@efdigital/ll-xceed-v2/utils/layout';
import { array } from '@ember/helper';
import { LinkTo } from '@ember/routing';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { pageTitle } from 'ember-page-title';

interface CoachingCoachCoacheesCoacheeSignature {
  Args: {
    model: CoachingCoachCoacheesCoacheeModel;
    controller: unknown;
  };
}

export default class CoachingCoachCoacheesCoachee extends Component<CoachingCoachCoacheesCoacheeSignature> {
  @service declare router: RouterService;

  /**
   * `aria-current="page"` for the view being shown, nothing for the rest.
   * `LinkTo` only sets an `active` class, which conveys nothing to a screen
   * reader. Arrow field because the template calls it.
   */
  ariaCurrent = (routeName: string): 'page' | undefined =>
    this.router.currentRouteName === routeName ? 'page' : undefined;

  get pastAttentionBadgeClass(): string {
    const active =
      this.router.currentRouteName === 'coaching.coach.coachees.coachee.past';

    return active
      ? 'badge rounded-pill bg-body text-primary ms-1 fw-semibold'
      : 'badge rounded-pill text-bg-warning ms-1 fw-semibold';
  }

  get coachingTimeValue() {
    return {
      count: this.args.model.summary.deliveredMinutes,
      more: false,
    };
  }

  get sessionsDeliveredValue() {
    return {
      count: this.args.model.summary.deliveredCount,
      more: false,
    };
  }

  get chemistryStatus() {
    return resolveChemistryStatus(
      this.args.model.coachee.status,
      this.args.model.coachee.sessions
    );
  }

  get chemistryStatusValue() {
    return {
      count:
        this.chemistryStatus === 'Chosen' ||
        this.chemistryStatus === 'Decision pending'
          ? 1
          : 0,
      more: false,
    };
  }

  <template>
    {{pageTitle @model.coachee.displayName}}

    <div class={{PAGE_WRAPPER}}>
      <LinkTo
        @route="coaching.coach.coachees"
        class="btn btn-link px-0 mb-3 d-inline-flex align-items-center gap-1 text-decoration-none"
      >
        <iconify-icon inline icon="ph:arrow-left" aria-hidden="true" />
        All coachees
      </LinkTo>

      <CoacheeNameCard
        @coachee={{@model.coachee}}
        @engagements={{@model.engagements}}
        class="mb-4"
      >
        <:actions>
          {{#if @model.messagePartnership}}
            <LinkTo
              @route="coaching.coach.messages.conversation"
              @models={{array @model.messagePartnership.id}}
              class="btn btn-outline-primary d-inline-flex align-items-center gap-2 flex-shrink-0"
              data-test-message-coachee
            >
              <iconify-icon icon="ph:chat-circle" aria-hidden="true" />
              Message
            </LinkTo>
          {{/if}}
        </:actions>
      </CoacheeNameCard>

      <div class="row row-cols-1 row-cols-md-3 g-3 mb-4">
        <div class="col">
          <DashboardStat
            @title="Coaching time"
            @value={{this.coachingTimeValue}}
            @display={{formatCoachingTime @model.summary.deliveredMinutes}}
            @detail="of {{formatCoachingTime @model.allocatedMinutes}}"
            @icon="ph:clock"
            @compact={{true}}
            class="h-100"
          />
        </div>
        <div class="col">
          <DashboardStat
            @title="Sessions delivered"
            @value={{this.sessionsDeliveredValue}}
            @icon="ph:check-circle"
            @compact={{true}}
            class="h-100"
          />
        </div>
        <div class="col">
          <DashboardStat
            @title="Chemistry status"
            @value={{this.chemistryStatusValue}}
            @display={{this.chemistryStatus}}
            @icon="ph:flask"
            @compact={{true}}
            class="h-100"
          />
        </div>
      </div>

      {{! Route links, not ARIA tabs: each view has its own URL, and real tab
          semantics would also owe a `tabpanel` and arrow-key handling that
          navigation links don't have. `aria-current` marks the active view.
          Always shown so goals and resources are reachable before any session
          is booked. Sessions waiting for an outcome report live under Past. }}
      <nav
        class="ll-sticky-subnav py-2 mb-2 overflow-x-auto"
        aria-label="Coachee views"
      >
        <ul
          class="nav nav-pills ll-bg-raised rounded-pill p-1 shadow-sm d-inline-flex flex-nowrap mb-0"
        >
          <li class="nav-item">
            <LinkTo
              @route="coaching.coach.coachees.coachee.about"
              class="nav-link rounded-pill px-3 px-lg-4 fw-semibold"
              aria-current={{this.ariaCurrent
                "coaching.coach.coachees.coachee.about"
              }}
            >About</LinkTo>
          </li>
          <li class="nav-item">
            <LinkTo
              @route="coaching.coach.coachees.coachee.index"
              class="nav-link rounded-pill px-3 px-lg-4 fw-semibold"
              aria-current={{this.ariaCurrent
                "coaching.coach.coachees.coachee.index"
              }}
            >Upcoming</LinkTo>
          </li>
          <li class="nav-item">
            <LinkTo
              @route="coaching.coach.coachees.coachee.canceled"
              class="nav-link rounded-pill px-3 px-lg-4 fw-semibold"
              aria-current={{this.ariaCurrent
                "coaching.coach.coachees.coachee.canceled"
              }}
            >Canceled</LinkTo>
          </li>
          <li class="nav-item">
            <LinkTo
              @route="coaching.coach.coachees.coachee.past"
              class="nav-link rounded-pill px-3 px-lg-4 fw-semibold"
              aria-current={{this.ariaCurrent
                "coaching.coach.coachees.coachee.past"
              }}
            >Past
              {{#if @model.summary.actionRequired.length}}
                <span class={{this.pastAttentionBadgeClass}}>
                  {{@model.summary.actionRequired.length}}
                  <span class="visually-hidden">need an outcome report</span>
                </span>
              {{/if}}
            </LinkTo>
          </li>
          <li class="nav-item">
            <LinkTo
              @route="coaching.coach.coachees.coachee.goals"
              class="nav-link rounded-pill px-3 px-lg-4 fw-semibold"
              aria-current={{this.ariaCurrent
                "coaching.coach.coachees.coachee.goals"
              }}
            >Goals</LinkTo>
          </li>
          <li class="nav-item">
            <LinkTo
              @route="coaching.coach.coachees.coachee.resources"
              class="nav-link rounded-pill px-3 px-lg-4 fw-semibold"
              aria-current={{this.ariaCurrent
                "coaching.coach.coachees.coachee.resources"
              }}
            >Resources</LinkTo>
          </li>
        </ul>
      </nav>

      {{outlet}}
    </div>
  </template>
}
